import React, { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Trophy, Save, Eye, RefreshCw, Lock, Unlock } from "lucide-react";

const formatINR2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function PrizesPanel() {
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get("/admin/prize-config"), api.get("/prizes")]);
      setForm(c.data);
      setPreview(p.data);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const recalcNow = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/recalculate-prizes");
      setPreview(data);
      toast.success("Prize amounts recalculated");
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const toggleFreeze = async () => {
    if (!preview) return;
    const next = !preview.frozen;
    if (next && !window.confirm("Freeze prizes permanently? Students will see 'Final prize amount' on all cards.")) return;
    setSaving(true);
    try {
      await api.post("/admin/prize-freeze", { frozen: next });
      toast.success(next ? "Prizes frozen" : "Prizes unfrozen");
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        prize_1_base: parseInt(form.prize_1_base) || 0,
        prize_2_base: parseInt(form.prize_2_base) || 0,
        prize_3_base: parseInt(form.prize_3_base) || 0,
        prize_1_pool_percent: parseFloat(form.prize_1_pool_percent) || 0,
        prize_2_pool_percent: parseFloat(form.prize_2_pool_percent) || 0,
        prize_3_pool_percent: parseFloat(form.prize_3_pool_percent) || 0,
      };
      await api.post("/admin/prize-config", payload);
      toast.success("Prize configuration saved");
      const { data: live } = await api.get("/prizes");
      setPreview(live);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setSaving(false); }
  };

  if (!form) return <div className="card-surface"><span className="spinner" /></div>;

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="prize-config-panel">
      <form onSubmit={submit} className="lg:col-span-3 card-surface">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-5 h-5 text-brand" strokeWidth={1.5} />
          <div className="font-display text-2xl text-white">Prize Configuration</div>
        </div>
        <p className="text-sm text-gray-500 mb-6">These settings are internal only — students only see the calculated prize amounts on the landing page.</p>

        {[
          { rank: "Champion (Rank 1)", base: "prize_1_base", pct: "prize_1_pool_percent" },
          { rank: "Runner-up (Rank 2)", base: "prize_2_base", pct: "prize_2_pool_percent" },
          { rank: "Third (Rank 3)", base: "prize_3_base", pct: "prize_3_pool_percent" },
        ].map((row) => (
          <div key={row.base} className="border-t border-white/5 py-4 first:border-t-0 first:pt-0">
            <div className="label-mono mb-3">{row.rank}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Base amount (₹)</label>
                <input type="number" min="0" value={form[row.base]} onChange={(e) => setField(row.base, e.target.value)}
                  className="input-field" data-testid={`input-${row.base}`} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Pool % per registration</label>
                <input type="number" min="0" max="100" step="0.1" value={form[row.pct]} onChange={(e) => setField(row.pct, e.target.value)}
                  className="input-field" data-testid={`input-${row.pct}`} />
              </div>
            </div>
          </div>
        ))}

        <button type="submit" disabled={saving} className="btn-primary mt-6 flex items-center gap-2" data-testid="prize-save-btn">
          {saving ? <span className="spinner" /> : <Save className="w-4 h-4" strokeWidth={1.5} />} Save Configuration
        </button>
      </form>

      <div className="lg:col-span-2 space-y-4" data-testid="prize-live-preview">
        <div className="card-surface border-l-2 border-brand">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-brand" strokeWidth={1.5} />
            <div className="font-display text-xl text-white">Live Preview</div>
          </div>
          <p className="text-xs text-gray-500 mb-4">What students see right now (based on {preview?.total_registrations ?? 0} paid registrations).{preview?.frozen ? <span className="ml-2 badge badge-accepted">frozen</span> : null}</p>

          {preview && (
            <div className="space-y-3">
              {[
                { l: "Champion",  v: preview.prize_1, k: "live-1" },
                { l: "Runner-up", v: preview.prize_2, k: "live-2" },
                { l: "Third",     v: preview.prize_3, k: "live-3" },
              ].map((x) => (
                <div key={x.k} className="border border-white/5 p-3 flex items-center justify-between" data-testid={x.k}>
                  <div className="label-mono">{x.l}</div>
                  <div className="font-display text-xl text-brand">{formatINR2(x.v)}</div>
                </div>
              ))}
            </div>
          )}

          <button type="button" onClick={recalcNow} disabled={saving} className="btn-secondary w-full mt-2 text-xs flex items-center justify-center gap-2" data-testid="prize-recalculate-btn">
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Recalculate Now
          </button>
          <button type="button" onClick={toggleFreeze} disabled={saving} className="btn-secondary w-full mt-2 text-xs flex items-center justify-center gap-2" data-testid="prize-freeze-btn">
            {preview?.frozen ? <><Unlock className="w-3.5 h-3.5" strokeWidth={1.5} /> Unfreeze Prizes</> : <><Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> Freeze Prizes</>}
          </button>
          <button type="button" onClick={load} className="btn-secondary w-full mt-2 text-xs" data-testid="prize-refresh-btn">Refresh Preview</button>
        </div>
      </div>
    </div>
  );
}
